import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeleteLmsTrainingModal } from 'src/components/lms-training/delete/DeleteLmsTrainingModal'
import { LmsTrainingFormModal } from 'src/components/lms-training/lms-training-form/LmsTrainingFormModal'
import type {
  LmsTrainingFormMode,
  LmsTrainingFormValues,
} from 'src/components/lms-training/lms-training-form/LmsTrainingForm.interface'
import { LmsTrainingsTable } from 'src/components/lms-training/lms-trainings-table/LmsTrainingsTable'
import { LmsTrainingsTableToolbar } from 'src/components/lms-training/lms-trainings-table/LmsTrainingsTableToolbar'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateLmsTraining,
  AdminDeleteLmsTraining,
  AdminFetchLmsTrainingCatalog,
  AdminUpdateLmsTraining,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import { getLmsTrainingDeletingId, getLmsTrainingSaving } from 'src/selectors/lmsTraining'
import { blankToUndefined } from 'src/utils/blankToUndefined'
import { GSIcon } from 'src/components/shared/GSIcon'

interface FormModalState {
  mode: LmsTrainingFormMode
  lmsTraining: LmsTrainingPlain | null
}

const LmsCoursesPageComponent = () => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button and by a successful create, update or delete — included in the
  // fetch effect's dependency array below so any of them re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [formModalState, setFormModalState] = useState<FormModalState | null>(null)
  const [coursePendingDelete, setCoursePendingDelete] = useState<LmsTrainingPlain | null>(null)

  const { isAuthenticated } = useAuthSession()
  const isSaving = useSelector(getLmsTrainingSaving)
  const deletingId = useSelector(getLmsTrainingDeletingId)
  // Add, Edit and Delete all call `lms_trainings_manage` endpoints (ENG-1207 part 2).
  const canManage = useHasPermission(InternalPermission.LmsTrainingsManage)

  /** The pending row's own in-flight state — drives both the dismissal guard and the modal. */
  const isDeletingPendingRow = coursePendingDelete !== null && deletingId === coursePendingDelete.id

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect: the endpoint takes no search or pagination params and no customer
  // scope, so this only reruns on an auth change or a manual refresh.
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchLmsTrainingCatalog.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  const handleOpenCreate = useCallback(() => {
    setFormModalState({ mode: 'create', lmsTraining: null })
  }, [])

  const handleOpenEdit = useCallback((lmsTraining: LmsTrainingPlain) => {
    setFormModalState({ mode: 'edit', lmsTraining })
  }, [])

  // Guarded on `isSaving`, which covers every dismissal path at once — Cancel, the modal's
  // close button, and a backdrop click. Without it, an admin could dismiss a saving form, open
  // another row, and have the first request's success handler close the SECOND dialog and
  // discard its unsaved input.
  const handleCloseFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setFormModalState(null)
  }, [isSaving])

  const handleOpenDelete = useCallback((lmsTraining: LmsTrainingPlain) => {
    setCoursePendingDelete(lmsTraining)
  }, [])

  // Guarded the same way, and this is also what keeps `deletingId` correct as a single value
  // rather than a set: only one row can be pending deletion at a time, and while its request is
  // in flight the modal cannot be dismissed — so a second delete cannot be started, and one
  // completing request can never clear another's in-flight state.
  const handleCloseDeleteModal = useCallback(() => {
    if (isDeletingPendingRow) {
      return
    }

    setCoursePendingDelete(null)
  }, [isDeletingPendingRow])

  const handleSubmit = useCallback(
    async (values: LmsTrainingFormValues) => {
      if (!formModalState) {
        return
      }

      // The single string-to-number boundary for externalId. The validator has already
      // guaranteed `/^\d+$/`, so this cannot produce NaN.
      const externalId = Number(values.externalId.trim())
      const url = values.url.trim()
      const title = values.title.trim()
      const description = blankToUndefined(values.description)
      const manualUploadRequirements = blankToUndefined(values.manualUploadRequirements)

      if (formModalState.mode === 'edit') {
        if (!formModalState.lmsTraining) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<LmsTrainingPlain>(
          AdminUpdateLmsTraining.request({
            lmsTrainingId: formModalState.lmsTraining.id,
            externalId,
            url,
            title,
            description,
            manualUploadRequirements,
          }),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Course updated', { variant: 'success' })
        setFormModalState(null)
        handleRefresh()

        return
      }

      const created = await asyncDispatchWithErrorToast<LmsTrainingPlain>(
        AdminCreateLmsTraining.request({
          externalId,
          url,
          title,
          description,
          manualUploadRequirements,
        }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Course added', { variant: 'success' })
      setFormModalState(null)
      handleRefresh()
    },
    [formModalState, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!coursePendingDelete) {
      return
    }

    // A course used by an onboarding template fails at the FK. The error toast has already been
    // raised — the modal stays open so the admin can read it against the row they picked.
    const deleted = await asyncDispatchWithErrorToast<string>(
      AdminDeleteLmsTraining.request({ lmsTrainingId: coursePendingDelete.id }),
    )

    if (!deleted) {
      return
    }

    snackbar?.enqueueSnackbar('Course deleted', { variant: 'success' })
    setCoursePendingDelete(null)
    handleRefresh()
  }, [coursePendingDelete, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-lms-courses-page" css={tw`min-w-0 flex-1 overflow-y-auto p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">LMS Courses</Typography>
          </div>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="lms-courses-list-add-button"
            >
              Add course
            </Button>
          ) : null}
        </div>
        <LmsTrainingsTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <LmsTrainingsTable
          searchQuery={searchQuery}
          canManage={canManage}
          onEditLmsTraining={handleOpenEdit}
          onDeleteLmsTraining={handleOpenDelete}
        />
      </div>
      <LmsTrainingFormModal
        isOpen={Boolean(formModalState)}
        mode={formModalState?.mode ?? 'create'}
        lmsTraining={formModalState?.lmsTraining ?? null}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseFormModal}
      />
      <DeleteLmsTrainingModal
        isOpen={Boolean(coursePendingDelete)}
        lmsTraining={coursePendingDelete}
        isDeleting={isDeletingPendingRow}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
      />
    </Fragment>
  )
}

export const LmsCoursesPage = memo(LmsCoursesPageComponent)
